import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { MarkAsSubmittedAction } from 'ngrx-forms';
import { filter, map, mergeMap } from 'rxjs/operators';
import { FehApiActions } from '@feh/shared-api';

import {
  continueFlowFromSolarPanelsWizardAction,
  handleSolarPanelsFunnelAddressNotFoundErrorAction,
  handleSolarPanelsFunnelPrivateErrorAction,
  navigateToHouseHasInsufficientRoofAreaPageAction,
  navigateToHouseIsMonumentPageAction,
  navigateToHouseIsUnsuitablePageAction,
  navigateToProductOverviewPageAction,
  navigateToResultOverviewPageAction,
  navigateToSuitabilityCheckPageAction,
  resetSolarPanelsProductSelectionAction,
  selectSolarPanelsFunnelSettings,
} from '../solar-panels-funnel';
import { resetSolarPanelsOrderFormAction } from '../solar-panels-order-form/solar-panels-order-form.actions';
import { hideSolarPanelsProductOverviewAction } from '../solar-panels-products';
import {
  hideSolarPanelsSuitabilityCheckFormAction,
  resetSuitabilityFormAction,
  showSolarPanelsSuitabilityCheckFormAction,
} from '../solar-panels-suitability-check';
import { solarPanelsWizardFormSubmitAction } from './solar-panels-wizard.actions';
import { solarPanelsWizardFormId } from './form-ids';
import { selectSolarPanelsWizardFormValues } from './solar-panels-wizard.selectors';

@Injectable()
export class SolarPanelsWizardEffects {
  private readonly store = inject(Store);
  private readonly actions = inject(Actions);

  funnelSettings$ = this.store.select(selectSolarPanelsFunnelSettings);
  solarPanelsWizardFormValues$ = this.store.select(
    selectSolarPanelsWizardFormValues
  );

  /**
   * On submit,
   * dispatch a mark as submitted action.
   * @todo: move to updatefn in reducer when the extending supports this.
   */
  public readonly submitSolarPanelsWizardForm$ = createEffect(() =>
    this.actions.pipe(
      ofType(solarPanelsWizardFormSubmitAction),
      map(() => new MarkAsSubmittedAction(solarPanelsWizardFormId))
    )
  );

  public readonly fallThroughToHouseIsUnsuitablePage$ = createEffect(() =>
    this.actions.pipe(
      ofType(solarPanelsWizardFormSubmitAction),
      concatLatestFrom(() => [
        this.solarPanelsWizardFormValues$,
        this.funnelSettings$,
      ]),
      // only break out when both the incorrect value is passed and a page is configured in sitecore.
      filter(
        ([, formValues, { houseIsUnsuitablePage }]) =>
          formValues?.houseType !== 'bought_with_own_roof' &&
          !!houseIsUnsuitablePage?.href
      ),
      map(([, , { houseIsUnsuitablePage }]) =>
        navigateToHouseIsUnsuitablePageAction({
          pageData: houseIsUnsuitablePage,
        })
      )
    )
  );

  /**
   * On submit,
   * reset everything &
   * dispatch address check.
   */
  public readonly submitSolarPanelsWizardFormSuccess$ = createEffect(() =>
    this.actions.pipe(
      ofType(solarPanelsWizardFormSubmitAction),
      concatLatestFrom(() => [
        this.solarPanelsWizardFormValues$,
        this.funnelSettings$,
      ]),
      // continue when:
      // formState is valid AND ( own roof, OR no fallthrough page configured - for backwards compatibility)
      filter(
        ([_, formValues, { houseIsUnsuitablePage }]) =>
          formValues?.houseType === 'bought_with_own_roof' ||
          !houseIsUnsuitablePage?.href
      ),
      mergeMap(([_, formValues, funnelSettings]) => {
        if (!funnelSettings.id) {
          return [
            handleSolarPanelsFunnelPrivateErrorAction({
              message: 'id is not defined on FunnelSettings',
            }),
          ];
        }

        const { address, consumption } = formValues;

        return [
          hideSolarPanelsSuitabilityCheckFormAction(),
          resetSuitabilityFormAction(),
          hideSolarPanelsProductOverviewAction(),
          resetSolarPanelsProductSelectionAction(),
          resetSolarPanelsOrderFormAction(),
          FehApiActions.getAddressinfoV1.requestAction({
            payload: {
              postalcode: address.postcode,
              housenumber: address.houseNumber.toString(),
              housenumberaddition: address.houseNumberAddition,
              consumption: parseInt(consumption),
              funnelsettingsId: funnelSettings.id,
            },
          }),
        ];
      })
    )
  );

  /**
   * Determines whether or not the flow can be continued after submission,
   * or if we need to be routed to a non-suitability page.
   */
  public readonly determineIfFlowCanContinueAfterSubmission$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(FehApiActions.getAddressinfoV1.successAction),
        concatLatestFrom(() => this.funnelSettings$),
        mergeMap(
          ([
            action,
            { houseIsMonumentPage, houseHasInsufficientRoofAreaPage },
          ]) => {
            const houseIsMonument = action.payload.criteria.isMonument;
            const houseHasInsufficientRoofArea =
              !action.payload.criteria.hasSufficientRoofArea;

            // If a house is monumental and a page is configured
            // for people to be sent to in this edge-case
            // the flow is aborted and the prospect is routed.
            if (houseIsMonument && houseIsMonumentPage?.href) {
              return [
                navigateToHouseIsMonumentPageAction({
                  pageData: houseIsMonumentPage,
                }),
              ];

              // If a house has insufficient roof area and a page is configured
              // for people to be sent to in this edge-case
              // the flow is aborted and the prospect is routed.
            } else if (
              houseHasInsufficientRoofArea &&
              houseHasInsufficientRoofAreaPage?.href
            ) {
              return [
                navigateToHouseHasInsufficientRoofAreaPageAction({
                  pageData: houseHasInsufficientRoofAreaPage,
                }),
              ];

              // In any other case, we can continue the flow.
            } else {
              return [continueFlowFromSolarPanelsWizardAction()];
            }
          }
        )
      )
  );

  /**
   * On success,
   * if the criteria pass,
   * and the suitability check step is enabled
   * dispatch an action that indicates we want to move to that page.
   */
  public readonly navigateToSuitabilityCheckPage$ = createEffect(() =>
    this.actions.pipe(
      ofType(continueFlowFromSolarPanelsWizardAction),
      concatLatestFrom(() => this.funnelSettings$),
      filter(([, { showSuitabilityCheckPage }]) => !!showSuitabilityCheckPage),
      mergeMap(([, { suitabilityCheckPage }]) => [
        showSolarPanelsSuitabilityCheckFormAction(),
        navigateToSuitabilityCheckPageAction({
          pageData: suitabilityCheckPage,
        }),
      ])
    )
  );

  /**
   * On success,
   * if the criteria pass,
   * and the suitability check page is disabled,
   * and the resultpage is enabled,
   * and the product page is disabled,
   * dispatch an action that indicates we want to move to that page.
   */
  public readonly navigateToResultOverviewPage$ = createEffect(() =>
    this.actions.pipe(
      ofType(continueFlowFromSolarPanelsWizardAction),
      concatLatestFrom(() => this.funnelSettings$),
      filter(
        ([, funnelSettings]) =>
          !funnelSettings.showSuitabilityCheckPage &&
          !funnelSettings.showProductOverviewPage &&
          !!funnelSettings.showResultOverviewPage
      ),
      map(([, { resultOverviewPage }]) =>
        navigateToResultOverviewPageAction({
          pageData: resultOverviewPage,
        })
      )
    )
  );

  /**
   * On success,
   * if the criteria pass,
   * and the suitability check page is disabled,
   * and the product page is enabled,
   * dispatch an action that indicates we want to move to that page.
   */
  public readonly navigateToProductOverviewPage$ = createEffect(() =>
    this.actions.pipe(
      ofType(continueFlowFromSolarPanelsWizardAction),
      concatLatestFrom(() => this.funnelSettings$),
      filter(
        ([, funnelSettings]) =>
          !funnelSettings.showSuitabilityCheckPage &&
          !!funnelSettings.showProductOverviewPage
      ),
      map(([, funnelSettings]) =>
        navigateToProductOverviewPageAction({
          pageData: funnelSettings.productOverviewPage,
        })
      )
    )
  );

  /**
   * On error,
   * if the action is a 404
   * dispatch an Address not found error action
   */
  public readonly onAddressCheckError$ = createEffect(() =>
    this.actions.pipe(
      ofType(FehApiActions.getAddressinfoV1.errorAction),
      filter((action) => action.payload.status === 404),
      map(() => handleSolarPanelsFunnelAddressNotFoundErrorAction())
    )
  );
}
